import { Badge } from '@/components/ui/badge';
import { CheckCircle2, Circle } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';

interface Substep {
  id: string;
  step_number: number;
  title: string;
  description: string;
  isCompleted: boolean;
}

interface SubstepsSidebarProps {
  substeps: Substep[];
  currentSubstepId: string;
  onSubstepClick: (substepId: string) => void;
}

/**
 * Sidebar showing all substeps of the current main step.
 * Allows navigation to any substep in any order (non-linear).
 *
 * Rendered inside WorkflowLayout's sidebar slot — no fixed positioning needed.
 */
export const SubstepsSidebar = ({
  substeps,
  currentSubstepId,
  onSubstepClick,
}: SubstepsSidebarProps) => {
  const { t } = useTranslation();

  if (substeps.length === 0) {
    return null;
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex-none p-4 border-b border-border bg-muted/50">
        <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
          {t('workflow.currentStepTasks')}
        </h3>
        <p className="text-xs text-muted-foreground mt-1">
          {t('workflow.anyOrderDescription')}
        </p>
      </div>

      <div className="flex-1 overflow-y-auto p-2 space-y-1">
        {substeps.map((substep) => {
          const isCurrent = substep.id === currentSubstepId;

          return (
            <button
              key={substep.id}
              onClick={() => onSubstepClick(substep.id)}
              className={cn(
                'w-full text-left p-3 rounded-lg transition-all duration-200',
                'hover:bg-accent/50 focus:outline-none focus:ring-2 focus:ring-primary',
                isCurrent && 'bg-primary/10 border-2 border-primary shadow-sm',
                !isCurrent && 'border border-border'
              )}
            >
              <div className="flex items-start gap-3">
                {/* Status Icon */}
                <div className="flex-shrink-0 mt-0.5">
                  {substep.isCompleted ? (
                    <CheckCircle2 className="h-5 w-5 text-success" />
                  ) : (
                    <Circle className="h-5 w-5 text-muted-foreground" />
                  )}
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span
                      className={cn(
                        'text-xs font-mono font-bold',
                        isCurrent ? 'text-primary' : 'text-muted-foreground'
                      )}
                    >
                      {substep.step_number}
                    </span>
                    {substep.isCompleted && (
                      <Badge
                        variant="outline"
                        className="text-[10px] h-4 px-1 bg-success/10 text-success border-success/20"
                      >
                        {t('instructions.done')}
                      </Badge>
                    )}
                  </div>

                  <h4
                    className={cn(
                      'text-sm font-semibold mb-1 line-clamp-2',
                      isCurrent ? 'text-foreground' : 'text-foreground/80'
                    )}
                  >
                    {substep.title}
                  </h4>

                  {/* Description preview - max 2 lines, gray */}
                  <div
                    className="text-xs text-muted-foreground line-clamp-2"
                    dangerouslySetInnerHTML={{
                      __html: substep.description
                        .replace(/<[^>]*>/g, '') // Strip HTML tags for preview
                        .substring(0, 100),
                    }}
                  />
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};
